import * as React from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

import { withIcon } from '../../core/components/Icon';
import classnames from '../../core/utils/classnames.tsx';
import { capitalizeFirstLetter } from '../../core/utils/string.ts';
import { useLocalStorage } from '../../core/utils/hooks/useLocalStorage';
import { formatDistanceToNow } from 'date-fns';

export interface Announcement {
    id: string;
    announcementType: 'info' | 'issue' | 'maintenance';
    title: string;
    message: string;
    titleDe?: string;
    messageDe?: string;
    externalLink?: string;
    startedAt: string;
    resolvedAt?: string;
}

const CloseIcon = withIcon(faXmark);

const useAnnouncementBar = (id: string) => {
    const [closedAnnouncements, setClosedAnnouncements] = useLocalStorage('closedAnnouncements', []);

    const isClosed = closedAnnouncements.includes(id);
    const close = () => {
        setClosedAnnouncements([...closedAnnouncements, id]);
    };

    return { isClosed, close };
};

const AnnouncementBar = ({ announcement }: { announcement: Announcement }) => {
    const { t } = useTranslation('announcements');

    const { isClosed, close } = useAnnouncementBar(announcement.id);

    let title = announcement.title;
    if (announcement[`title${capitalizeFirstLetter(i18n.language)}`]) {
        title = announcement[`title${capitalizeFirstLetter(i18n.language)}`];
    }

    let message = announcement.message;
    if (announcement[`message${capitalizeFirstLetter(i18n.language)}`]) {
        message = announcement[`message${capitalizeFirstLetter(i18n.language)}`];
    }

    const startedAt = announcement.startedAt ? new Date(announcement.startedAt) : null;
    const announcementType = announcement.announcementType.toLowerCase();

    if (isClosed) {
        return null;
    }

    return (
        <div
            className={classnames(
                'w-full px-7 pr-16 py-4 text-sm flex flex-wrap justify-center items-center gap-4 bg-surface-strong border-b border-solid border-line-weak relative',
                announcementType == 'info' && 'bg-surface-success-weak text-content-success-strong',
                announcementType == 'maintenance' && 'bg-surface-warning-weak text-content-warning-strong',
                announcementType == 'issue' && 'bg-surface-danger-weak text-content-danger-strong'
            )}
        >
            <div className="justify-center">
                <span className="font-medium inline-block mr-2">{title}</span>
                <span>{message}</span>
                {announcement.externalLink && (
                    <a
                        className="underline !text-content-brand-default hover:!text-content-brand-hover ml-2"
                        target="_blank"
                        href={announcement.externalLink}
                    >
                        {t('announcementBar.learnMore')}
                    </a>
                )}
                {startedAt && (
                    <span className="text-xs text-content-medium inline-block ml-2">
                        {formatDistanceToNow(startedAt, { addSuffix: true })}
                    </span>
                )}{' '}
            </div>

            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <button
                    className="flex items-center justify-center w-8 h-8 hover:bg-surface-strong rounded transition-colors duration-200"
                    onClick={close}
                >
                    <CloseIcon />
                </button>
            </div>
        </div>
    );
};

export default AnnouncementBar;
